import { useCallback, useEffect, useState, useRef } from "react";
import * as d3 from "d3";
import useMeasure from "react-use-measure";
import {
  endOfMonth,
  format,
  startOfMonth,
  eachMonthOfInterval,
} from "date-fns";

const ProgressionGraph = ({
  exerciseSessions,
  onChartItemClicked,
  selectedGraphType,
  selectedWorkout,
}) => {
  const [chartData, setChartData] = useState(null);
  let [ref, bounds] = useMeasure();
  const [yAxisWidth, setYAxisWidth] = useState(0);

  /* TODO: 
  figure out a way to memoize the calculated graph values
  or move them to a parent component as the value calculations are repeated
  in ExerciseProgressionCard.jsx
  */
  const createChartData = useCallback(() => {
    const chartData = exerciseSessions?.map((exerciseSession) => {
      const workoutDate = new Date(exerciseSession.date);

      if (selectedGraphType === "avg-weight") {
        let totalWeight = 0;
        exerciseSession.sets.forEach((set) => {
          totalWeight = totalWeight + parseFloat(set.weight || 0);
        });
        let averageWeight = totalWeight / exerciseSession.sets.length;
        averageWeight =
          Math.round((averageWeight + Number.EPSILON) * 100) / 100;

        return {
          date: workoutDate,
          workoutData: averageWeight,
          workoutId: exerciseSession.parentWorkout.id,
        };
      }

      if (selectedGraphType === "avg-reps") {
        let totalreps = 0;
        exerciseSession.sets.forEach((set) => {
          totalreps = totalreps + parseFloat(set.reps || 0);
        });
        let averageReps = totalreps / exerciseSession.sets.length;
        averageReps = Math.round((averageReps + Number.EPSILON) * 100) / 100;

        return {
          date: workoutDate,
          workoutData: averageReps,
          workoutId: exerciseSession.parentWorkout.id,
        };
      }

      if (selectedGraphType === "avg-volume") {
        let totalVolume = 0;
        exerciseSession.sets.forEach((set) => {
          totalVolume =
            totalVolume +
            parseFloat(set.weight || 0) * parseFloat(set.reps || 0);
        });
        let averageVolume = totalVolume / exerciseSession.sets.length;
        averageVolume =
          Math.round((averageVolume + Number.EPSILON) * 100) / 100;

        return {
          date: workoutDate,
          workoutData: averageVolume,
          workoutId: exerciseSession.parentWorkout.id,
        };
      }

      if (selectedGraphType === "total-volume") {
        let totalVolume = 0;
        exerciseSession.sets.forEach((set) => {
          totalVolume =
            totalVolume +
            parseFloat(set.weight || 0) * parseFloat(set.reps || 0);
        });

        return {
          date: workoutDate,
          workoutData: totalVolume,
          workoutId: exerciseSession.parentWorkout.id,
        };
      }
    });

    setChartData(chartData);
  }, [exerciseSessions, selectedGraphType]);

  useEffect(() => {
    if (exerciseSessions && selectedGraphType) {
      createChartData(exerciseSessions);
    }
  }, [createChartData, exerciseSessions, selectedGraphType]);

  if (!chartData) return;

  const tableWidthBound = bounds.width;
  let startDay = startOfMonth(chartData.at(0).date);
  let endDay = endOfMonth(chartData.at(-1).date);
  let months = eachMonthOfInterval({ start: startDay, end: endDay });
  const chartWidth = Math.max(tableWidthBound - yAxisWidth, months.length * 80);

  return (
    <div className="overflow-x-scroll relative pb-2">
      <div className="h-0 w-full" ref={ref}></div>
      <ChartYAxis
        data={chartData}
        width={yAxisWidth}
        setWidth={setYAxisWidth}
        height={175}
      />
      <div style={{ width: `${chartWidth}px`, marginLeft: `${yAxisWidth}px` }}>
        <ChartInner
          data={chartData}
          width={chartWidth}
          height={175}
          onChartItemClicked={onChartItemClicked}
          selectedWorkout={selectedWorkout}
        />
      </div>
    </div>
  );
};

const ChartInner = ({
  data,
  width,
  height,
  onChartItemClicked,
  selectedWorkout,
}) => {
  let margin = {
    top: 10,
    right: 4,
    bottom: 20,
    left: 4,
  };

  let startDay = startOfMonth(data.at(0).date);
  let endDay = endOfMonth(data.at(-1).date);
  let months = eachMonthOfInterval({ start: startDay, end: endDay });

  let xScale = d3
    .scaleTime()
    .domain([startDay, endDay])
    .range([margin.left, width - margin.right]);

  let yScale = d3
    .scaleLinear()
    .domain(d3.extent(data.map((d) => d.workoutData)))
    .range([height - margin.bottom, margin.top]);

  let line = d3
    .line()
    .x((d) => xScale(d.date))
    .y((d) => yScale(d.workoutData));
  let d = line(data);

  const handleCircleClick = (d) => {
    onChartItemClicked(d.workoutId);
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`}>
      <path
        className="text-indigo-500"
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      />

      {yScale.ticks(5).map((avgWeight) => (
        <g
          transform={`translate(0,${yScale(avgWeight)})`}
          className="text-gray-500"
          key={avgWeight}
        >
          <line
            x1={margin.left}
            x2={width - margin.right}
            stroke="currentColor"
            strokeWidth={0.5}
            strokeDasharray={[1, 3]}
          />
        </g>
      ))}

      {months.map((month, i) => (
        <g
          transform={`translate(${xScale(month)}, ${height - 3})`}
          className="text-gray-500"
          key={month}
        >
          <text
            x={(xScale(endOfMonth(month)) - xScale(month)) / 2}
            className="text-[10px]"
            fill="currentColor"
          >
            {format(month, "MMM yy")}
          </text>
        </g>
      ))}

      {data.map((d, index) =>
        d.workoutId === selectedWorkout ? (
          <circle
            id={`progression-graph-circle-${d.workoutId}`}
            key={index}
            transform={`translate(${xScale(d.date)}, ${yScale(d.workoutData)})`}
            className="text-indigo-500"
            fill="currentColor"
            stroke="white"
            strokeWidth={2}
            r={5}
          />
        ) : (
          <g
            id={`progression-graph-circle-${d.workoutId}`}
            key={index}
            transform={`translate(${xScale(d.date)}, ${yScale(d.workoutData)})`}
          >
            <circle
              className="text-indigo-500"
              fill="white"
              stroke="currentColor"
              strokeWidth={1}
              r={3}
            />
            <circle
              r={6}
              className="text-indigo-100"
              fill="transparent"
              stroke="transparent"
              strokeWidth="10"
              pointerEvents="all"
              onClick={() => handleCircleClick(d)}
            />
          </g>
        )
      )}
    </svg>
  );
};

const ChartYAxis = ({ data, width, setWidth, height }) => {
  const svgRef = useRef();
  const textRefs = useRef([]);

  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: 0,
  };

  const yScale = d3
    .scaleLinear()
    .domain(d3.extent(data.map((d) => d.workoutData)))
    .range([height - margin.bottom, margin.top]);

  useEffect(() => {
    if (svgRef.current) {
      const maxWidth = Math.max(
        ...textRefs.current.map((ref) => ref?.getBBox().width || 0)
      );
      setWidth(maxWidth);
    }
  }, [data, height, setWidth]);

  return (
    <svg
      className=" fixed left-[7px] z-10 bg-gray-50"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      ref={svgRef}
    >
      {yScale.ticks(5).map((avgWeight, index) => (
        <g
          transform={`translate(0,${yScale(avgWeight)})`}
          className="text-gray-500"
          key={avgWeight}
        >
          <text
            alignmentBaseline="middle"
            className="text-[10px]"
            fill="currentColor"
            ref={(el) => (textRefs.current[index] = el)}
          >
            {avgWeight}
          </text>
        </g>
      ))}
    </svg>
  );
};

export default ProgressionGraph;
